import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import {
  DeleteButton,
  fetchUtils,
  useNotify,
  useResourceContext,
} from 'react-admin';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import Alert from '@material-ui/lab/Alert';

import { getUser } from '../../user';

const { fetchJson } = fetchUtils;

// Plan 061 (suppression gardée) : même posture que
// Vivier/components/DeleteFicheDialog.js — cookie de session same-origin,
// react-admin fetchUtils.fetchJson (rejette avec { status, body, message }
// sur un statut non-2xx), pas de bearer token (authProvider = cookie de
// session, voir authProvider.js).
const DELETE_BLOCKERS_URL = 'api/rpc/delete_blockers';
const FORCE_DELETE_URL = 'api/rpc/force_delete';

const MIN_REASON_LENGTH = 10;

// Traduction des codes stables rendus par force_delete (plan 061 §4.2). Le
// contenu vient du serveur ; ce dictionnaire ne fait que le rendre lisible,
// à l'identique de DeleteFicheDialog.js pour delete_vivier_fiche.
const FORCE_DELETE_ERROR_MESSAGES = {
  force_delete_unknown_resource:
    'Suppression impossible : ressource non prise en charge par le contournement.',
  force_delete_not_allowed:
    "Le contournement n'est pas autorisé pour cette ressource.",
  force_delete_reason_required: `Le motif doit compter au moins ${MIN_REASON_LENGTH} caractères.`,
  force_delete_not_found: "Suppression impossible : cette ligne n'existe plus.",
};

// Ressources enrôlées au contournement de suppression (plan 061 §4.3). La
// clé est le nom de resource react-admin ; la valeur est l'identifiant
// attendu par care.delete_blockers / care.force_delete — il peut différer
// (la fiche cleaner s'appelle "cleaner" côté react-admin mais
// "cleaner_contact_rw" côté care). Enrôler une nouvelle ressource se fait
// en ajoutant une ligne ici, rien d'autre à câbler côté front.
export const GUARDED_DELETE_RESOURCES = {
  cleaner_contract: 'cleaner_contract',
  cleaner: 'cleaner_contact_rw',
};

// Longueur max du detail affiché. Mesuré en production le 21/08/2026 sur la
// fiche `0031n00002cfzL4AAI` : le detail des affectations fait 3 513
// caractères (175 identifiants), et le bandeau complet atteignait 3 781
// caractères — le message utile (« 175 affectations seraient rendues
// orphelines ») y était illisible. Le serveur continue de rendre la liste
// ENTIÈRE, et care.force_delete la journalise intégralement dans
// force_delete_log.blockers : c'est elle qui rend une réparation possible
// (plan 061 §7, garde-corps ②). On ne tronque donc QUE l'affichage.
const MAX_DETAIL_LENGTH = 120;

const truncateDetail = (detail) => {
  if (detail.length <= MAX_DETAIL_LENGTH) {
    return detail;
  }
  // Coupe sur le dernier séparateur pour ne jamais afficher un identifiant
  // tronqué en deux, ce qui se lirait comme un identifiant faux.
  const cut = detail.slice(0, MAX_DETAIL_LENGTH);
  const lastSeparator = cut.lastIndexOf(', ');
  return `${lastSeparator > 0 ? cut.slice(0, lastSeparator) : cut}, …`;
};

// Bandeau : le COMPTE seul. C'est l'information que l'opérateur doit lire
// d'un coup d'œil (« 23 contrats seraient rendus orphelins ») ; la liste
// d'identifiants la noierait.
const formatBlockerShort = (blocker) => {
  return blocker.label;
};

// Dialogue : le compte, plus un échantillon d'identifiants borné.
const formatBlocker = (blocker) => {
  return blocker.detail
    ? `${blocker.label} (${truncateDetail(blocker.detail)})`
    : blocker.label;
};

// care.delete_blockers est la source unique de la règle de blocage
// (plan §4.1, "Règle : source unique") : ce hook se contente de la lire,
// il ne la réévalue jamais côté client — aucune comparaison de end_date,
// procedure_id ou created_at ici. Repli : tout échec (réseau, 404 tant que
// le back n'est pas livré, 5xx) résout sur "non protégé" pour ne jamais
// casser l'écran d'édition (plan lot D : "un 404 sur la RPC ne doit pas
// casser l'écran").
export const useDeleteBlockers = (resource, pk) => {
  const [blockers, setBlockers] = useState(null);

  useEffect(() => {
    if (!resource || pk == null) {
      setBlockers(null);
      return undefined;
    }

    let cancelled = false;
    setBlockers(null);

    const url = `${DELETE_BLOCKERS_URL}?p_resource=${encodeURIComponent(
      resource,
    )}&p_id=${encodeURIComponent(pk)}`;

    fetchJson(url, { method: 'GET', credentials: 'same-origin' })
      .then(({ json }) => {
        if (!cancelled) {
          setBlockers(json);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBlockers({ deletable: true, forceable: false, blockers: [] });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [resource, pk]);

  return blockers;
};

// Bandeau "au-dessus de la barre d'outils" (plan lot D, point 1). Rendu par
// FormToolbar comme un frère placé AVANT le <Toolbar> react-admin (qui est
// un flex row dédié à Save/Delete), pour garantir la position par le flux
// normal du document plutôt que par un habillage CSS.
export const DeleteBlockersBanner = ({ blockers }) => {
  if (!blockers || blockers.deletable !== false) {
    return null;
  }

  const reasons = blockers.blockers || [];
  const sentence =
    reasons.length > 0
      ? reasons.map(formatBlockerShort).join(' ; ')
      : 'cette ligne est protégée.';

  return (
    <Alert severity="warning" style={{ marginBottom: 8 }}>
      Suppression bloquée : {sentence}
    </Alert>
  );
};

DeleteBlockersBanner.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  blockers: PropTypes.object,
};

DeleteBlockersBanner.defaultProps = {
  blockers: null,
};

// Bouton générique, paramétré par `resource` (identifiant côté registre
// care.delete_guard_registry) et `pk` (identifiant de la ligne). Remplace
// <DeleteButton> dans FormToolbar pour les ressources enrôlées
// (GUARDED_DELETE_RESOURCES) — voir plan 061 lot D. `blockers` est fourni
// par l'appelant (FormToolbar, via useDeleteBlockers) pour n'interroger
// care.delete_blockers qu'une seule fois par écran, partagée avec le
// bandeau.
const GuardedDeleteButton = ({ resource, pk, blockers, record, redirect }) => {
  const notify = useNotify();
  const history = useHistory();
  const raResource = useResourceContext();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [nameConfirmation, setNameConfirmation] = useState('');
  const [forcing, setForcing] = useState(false);

  if (pk == null) {
    return null;
  }

  const isBlocked = !!(blockers && blockers.deletable === false);

  // Ligne non protégée (ou statut encore inconnu le temps du chargement,
  // cf. repli de useDeleteBlockers) : comportement react-admin habituel,
  // désormais forcé en mode pessimistic. C'est la correction du défaut
  // d'origine — bouton undoable, échec silencieux (plan §1) — et elle
  // s'applique indépendamment du reste de ce composant.
  if (!isBlocked) {
    return (
      <DeleteButton
        record={record}
        resource={raResource}
        redirect={redirect}
        mutationMode="pessimistic"
      />
    );
  }

  const composedName = [record?.firstname, record?.lastname]
    .filter(Boolean)
    .join(' ')
    .trim();
  const recordDisplayName = record?.fullname || composedName || String(pk);

  // Gate d'AFFICHAGE seulement : function === 'Admin' ne fait qu'afficher
  // ou masquer l'action "Supprimer quand même". Le contrôle réel est le
  // 403 renvoyé par le proxy sur POST rpc/force_delete pour tout compte
  // non-Admin — même posture que le gate RH/Admin de
  // Vivier/components/DeleteFicheDialog.js.
  const user = getUser();
  const canForceOption = !!(
    blockers.forceable &&
    user &&
    user.function === 'Admin'
  );

  const requiresName = blockers.confirm_mode === 'reason_and_name';
  const reasonValid = reason.trim().length >= MIN_REASON_LENGTH;
  const nameValid =
    !requiresName || nameConfirmation.trim() === recordDisplayName.trim();
  const canSubmitForce = canForceOption && reasonValid && nameValid && !forcing;

  // Phrases précalculées en JS plutôt qu'en JSX multiligne : évite toute
  // ambiguïté sur la fusion des espaces entre lignes JSX (chaque <div> ne
  // porte qu'une chaîne déjà complète).
  const blockedElementSentence = blockers.label
    ? `Élément concerné : ${blockers.label}.`
    : '';
  const orphanWarning = requiresName
    ? ' Cette action ne supprime que cette fiche : ce qui en dépend (contrats, affectations) ne sera pas supprimé et deviendra orphelin.'
    : '';
  const adminWarningIntro = `⚠️ Un compte Admin peut passer outre ces raisons.${orphanWarning}`;

  const closeDialog = () => {
    if (forcing) {
      return;
    }
    setDialogOpen(false);
    setReason('');
    setNameConfirmation('');
  };

  const handleForceDelete = async () => {
    setForcing(true);
    try {
      const headers = new Headers();
      headers.set('Content-Type', 'application/json');
      await fetchJson(FORCE_DELETE_URL, {
        method: 'POST',
        credentials: 'same-origin',
        headers,
        body: JSON.stringify({
          p_resource: resource,
          p_id: pk,
          p_reason: reason,
        }),
      });
      notify('Suppression effectuée.', 'info');
      setDialogOpen(false);
      history.push(typeof redirect === 'string' ? redirect : `/${raResource}`);
    } catch (err) {
      const status = err && err.status;
      const body = err && err.body;
      const backendMessage = (body && body.message) || (err && err.message);

      if (status === 403) {
        notify('Action réservée aux administrateurs.', 'warning');
      } else if (
        backendMessage &&
        FORCE_DELETE_ERROR_MESSAGES[backendMessage]
      ) {
        notify(FORCE_DELETE_ERROR_MESSAGES[backendMessage], 'warning');
      } else {
        notify('Erreur lors de la suppression', 'error');
      }
    } finally {
      setForcing(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => {
          setDialogOpen(true);
        }}
        style={{ color: '#f44336' }}
        startIcon={<DeleteIcon />}
      >
        Supprimer
      </Button>
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>Suppression bloquée</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {blockedElementSentence && <div>{blockedElementSentence}</div>}
            <div>Raisons :</div>
            <ul>
              {(blockers.blockers || []).map((blocker) => {
                return <li key={blocker.code}>{formatBlocker(blocker)}</li>;
              })}
            </ul>
          </DialogContentText>
          {canForceOption && (
            <>
              <DialogContentText component="div">
                <div>{adminWarningIntro}</div>
                <div>
                  {'Action '}
                  <strong>irréversible</strong>, tracée avec le motif
                  ci-dessous.
                </div>
              </DialogContentText>
              <TextField
                label={`Motif (${MIN_REASON_LENGTH} caractères minimum)`}
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value);
                }}
                fullWidth
                multiline
                disabled={forcing}
                margin="dense"
              />
              {requiresName && (
                <TextField
                  label={`Recopier « ${recordDisplayName} » pour confirmer`}
                  value={nameConfirmation}
                  onChange={(event) => {
                    setNameConfirmation(event.target.value);
                  }}
                  fullWidth
                  disabled={forcing}
                  margin="dense"
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={forcing}>
            Fermer
          </Button>
          {canForceOption && (
            <Button
              onClick={handleForceDelete}
              variant="contained"
              disabled={!canSubmitForce}
              style={{
                backgroundColor: canSubmitForce ? '#f44336' : undefined,
                color: '#fff',
              }}
              startIcon={
                forcing ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              Supprimer quand même
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

GuardedDeleteButton.propTypes = {
  resource: PropTypes.string.isRequired,
  pk: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // eslint-disable-next-line react/forbid-prop-types
  blockers: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  record: PropTypes.object,
  redirect: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
    PropTypes.func,
  ]),
};

GuardedDeleteButton.defaultProps = {
  pk: undefined,
  blockers: null,
  record: undefined,
  redirect: undefined,
};

export default GuardedDeleteButton;
